import { type CSSProperties, useState } from "react";
import { Form, Input } from "antd";
import { type InputProps } from "antd/lib";
import parsePhoneNumberFromString, {
  AsYouType,
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from "libphonenumber-js";

import { Select } from "@av/components/atoms/Select";

import type { AvE2EProps } from "../types";
import type { DefaultOptionType } from "antd/es/select";

const DEFAULT_MAX_PHONE_LIMIT = 14;

/**
 *
 * Prioritizes a set of supported locales so their calling codes appear first
 * when building the map. Ensures that each calling code maps to only one country.
 *
 */
function getUniqueCallingCodeOptions(): DefaultOptionType[] {
  // Trogdor Supported locales
  const supportedLocalesWithUniqueCountryCodes: CountryCode[] = ["CA", "FR", "ES", "GB", "JP"];

  const allCountries = getCountries();

  // Separate supported and remaining countries
  const remainingCountries = allCountries.filter(
    (c) => !supportedLocalesWithUniqueCountryCodes.includes(c)
  );

  const sortedRemainingCountries = [...remainingCountries].sort(
    (a, b) => Number(getCountryCallingCode(a)) - Number(getCountryCallingCode(b))
  );

  const orderedCountries = [...supportedLocalesWithUniqueCountryCodes, ...sortedRemainingCountries];

  const callingCodeToCountries = new Set<string>();

  orderedCountries.forEach((country) => {
    const code = getCountryCallingCode(country);

    callingCodeToCountries.add(`+${code}`);
  });

  return Array.from(callingCodeToCountries).map((code) => ({ label: code, value: code }));
}

const callingCodeOptions: DefaultOptionType[] = getUniqueCallingCodeOptions();

interface PhoneInputProps extends AvE2EProps, Omit<InputProps, "onChange"> {
  value?: string;
  onChange?: (value: string | undefined) => void;
  name: string;
  countryCodeSelectorStyles?: CSSProperties;
}

function PhoneInput(props: PhoneInputProps) {
  const {
    value,
    onChange,
    placeholder,
    testIdPrefix,
    size = "middle",
    name,
    countryCodeSelectorStyles,
    ...rest
  } = props;

  const defaultCountryCallingCode = getCountryCallingCode("CA");
  const form = Form.useFormInstance();

  const parsed = isValidPhoneNumber(value || "")
    ? parsePhoneNumberFromString(value || "")
    : undefined;

  const [countryCode, setCountryCode] = useState<string>(
    `+${parsed?.countryCallingCode || defaultCountryCallingCode}`
  );

  function computeDisplayValue() {
    const isValid = isValidPhoneNumber(value || "");

    const parsed = isValid
      ? parsePhoneNumberFromString(value || "", { defaultCallingCode: countryCode })
      : undefined;

    if (parsed) {
      return parsed.formatNational();
    }

    const rawValue = value ? value.replace(countryCode, "") : "";
    const formatter = new AsYouType();

    return formatter.input(rawValue);
  }

  const displayValue: string = computeDisplayValue();

  const onAreaCodeChange = (newCallingCode: string) => {
    setCountryCode(newCallingCode);
    const sanitizedPhoneNumber = displayValue.replace(/\D/g, "");

    form?.setFieldValue(name, `${newCallingCode}${sanitizedPhoneNumber}`);
  };

  const customOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedPhoneNumber = e.target.value.replace(/\D/g, "");
    const newPhoneNumber = sanitizedPhoneNumber ? `${countryCode}${sanitizedPhoneNumber}` : "";

    onChange?.(newPhoneNumber);
  };

  return (
    <Input
      addonBefore={
        <Select
          options={callingCodeOptions}
          styles={{ root: { width: 100, ...countryCodeSelectorStyles } }}
          value={countryCode}
          onChange={onAreaCodeChange}
          testIdPrefix={`${testIdPrefix}-phone-selector`}
        />
      }
      placeholder={placeholder ?? "--- --- ----"}
      value={displayValue}
      onChange={customOnChange}
      maxLength={DEFAULT_MAX_PHONE_LIMIT}
      data-testid={`${testIdPrefix}-phone-input`}
      size={size}
      name={name}
      {...rest}
      type="tel"
    />
  );
}

export { PhoneInput };
